import { useRef, useState } from "react";
import { Outlet, useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../auth/auth-context";
import { AppRoutes } from "../app/routes";
import { useScrollResetOnNavigate } from "../app/use-scroll-reset-on-navigate";
import "./ambassador-shell.css";

const AccountIcon = () => (
  <svg width="26" height="26" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle cx="12" cy="8" r="3.4" stroke="currentColor" strokeWidth="1.6" />
    <path
      d="M5 19c1.2-3 3.8-4.5 7-4.5s5.8 1.5 7 4.5"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
    />
  </svg>
);

/**
 * The embajador app-shell: the mobile chrome that wraps every ambassador
 * screen. Screens render into `<Outlet/>`; the shell owns the brand bar and
 * the account menu (sign out). Loaded as its own lazy chunk so field users
 * never download backoffice code (D-5 / E0.3).
 */
export const AmbassadorShell = () => {
  const { t } = useTranslation("shell");
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const contentRef = useRef<HTMLElement>(null);
  useScrollResetOnNavigate(contentRef);

  const handleSignOut = async () => {
    await signOut();
    navigate(AppRoutes.LOGIN, { replace: true });
  };

  return (
    <div className="ambassador-shell">
      <div className="ambassador-shell-frame">
        <header className="ambassador-shell-bar">
          <div className="ambassador-shell-brand">
            <img
              className="ambassador-shell-brand-logo"
              src="/vanacard-logo.svg"
              alt="VanaCard"
            />
          </div>

          <div className="ambassador-shell-account">
            <button
              type="button"
              className="ambassador-shell-account-button"
              aria-label={t("account")}
              aria-expanded={menuOpen}
              onClick={() => setMenuOpen((open) => !open)}
            >
              <AccountIcon />
            </button>

            {menuOpen && (
              <div className="ambassador-shell-menu" role="menu">
                <span className="ambassador-shell-menu-name">{user?.name}</span>
                <span className="ambassador-shell-menu-email">{user?.email}</span>
                <div className="ambassador-shell-menu-divider" />
                <button
                  type="button"
                  className="ambassador-shell-menu-action"
                  role="menuitem"
                  onClick={handleSignOut}
                >
                  {t("sign_out")}
                </button>
              </div>
            )}
          </div>
        </header>

        <main className="ambassador-shell-content" ref={contentRef}>
          <Outlet />
        </main>
      </div>
    </div>
  );
};
